import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { attorneyName } from '@/lib/caseFormatting';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, Calendar, Clock, User, Phone, Mail,
  CheckCircle, XCircle, AlertCircle, Edit, Trash2, Cloud
} from 'lucide-react';


const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const Appointments = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [stats, setStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [clients, setClients] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [appointmentsRes, attorneysRes, statsRes, clientsRes] = await Promise.all([
        api.get(`/appointments`),
        api.get(`/attorneys`),
        api.get(`/appointments/stats/summary`),
        api.get(`/clients`).catch(() => ({ data: [] })),
      ]);
      setAppointments(appointmentsRes.data);
      setAttorneys(attorneysRes.data);
      setStats(statsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner'].includes(user.role)) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const confirmAppointment = async (id) => {
    try {
      await api.put(`/appointments/${id}/confirm`, {});
      toast.success('Appointment confirmed and notification sent');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm appointment');
    }
  };

  const cancelAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      await api.put(`/appointments/${id}/cancel`, {});
      toast.success('Appointment cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Delete this appointment permanently?')) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success('Appointment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const clientName = (apt) =>
    clients.find((c) => c.id === apt.client_id)?.display_name || apt.client_name || '—';

  const filteredAppointments = appointments.filter(apt => {
    return filterStatus === 'all' || apt.status === filterStatus;
  });

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Appointments"
        titleTestId="appointments-title"
        actions={
          <Button onClick={() => navigate('/appointments/new')} data-testid="new-appointment-btn">
            <Plus size={20} /> New Appointment
          </Button>
        }
      />

      {/* Stats Cards */}
      <StatsGrid cols={5}>
        <StatCard icon={Calendar} iconClassName="text-primary" value={stats.total || 0} label="Total" onClick={() => setFilterStatus('all')} />
        <StatCard icon={AlertCircle} iconClassName="text-yellow-500" valueClassName="text-yellow-600" value={stats.pending || 0} label="Pending" onClick={() => setFilterStatus('pending')} />
        <StatCard icon={CheckCircle} iconClassName="text-blue-500" valueClassName="text-blue-600" value={stats.confirmed || 0} label="Confirmed" onClick={() => setFilterStatus('confirmed')} />
        <StatCard icon={CheckCircle} iconClassName="text-green-500" valueClassName="text-green-600" value={stats.completed || 0} label="Completed" onClick={() => setFilterStatus('completed')} />
        <StatCard icon={Clock} valueClassName="text-accent" value={stats.upcoming_week || 0} label="This Week" />
      </StatsGrid>

      {/* Filter Tabs */}
      <Tabs value={filterStatus} onValueChange={setFilterStatus} className="space-y-6">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
          </TabsList>

          <TabsContent value={filterStatus}>
            {filteredAppointments.length > 0 ? (
              <div className="space-y-4">
                {filteredAppointments.map((apt) => (
                  <Card key={apt.id} data-testid={`appointment-card-${apt.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={statusColors[apt.status]}>
                              {apt.status}
                            </Badge>
                            <Badge variant="outline">
                              {apt.service_type.replace('_', ' ')}
                            </Badge>
                            {apt.outlook_event_id && (
                              <Badge variant="outline" className="text-blue-600 border-blue-300">
                                <Cloud size={12} className="mr-1" /> Outlook
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-serif text-xl font-semibold text-primary mb-2">
                            {clientName(apt)}
                          </h3>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(apt.appointment_date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {new Date(apt.appointment_date).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              ({apt.duration_minutes} min)
                            </span>
                            {apt.client_email && (
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {apt.client_email}
                              </span>
                            )}
                            {apt.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} />
                                {apt.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {attorneyName(attorneys, apt.assigned_to)}
                            </span>
                          </div>
                          {apt.notes && (
                            <p className="mt-2 text-sm text-muted-foreground bg-secondary p-2 rounded">
                              {apt.notes}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {apt.status === 'pending' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => confirmAppointment(apt.id)}
                            >
                              <CheckCircle size={16} className="mr-1" /> Confirm
                            </Button>
                          )}
                          {['pending', 'confirmed'].includes(apt.status) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cancelAppointment(apt.id)}
                            >
                              <XCircle size={16} className="mr-1" /> Cancel
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/appointments/${apt.id}/edit`)}
                            aria-label={`Edit appointment for ${clientName(apt)}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteAppointment(apt.id)}
                            aria-label={`Delete appointment for ${clientName(apt)}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState icon={Calendar} message="No appointments found" />
            )}
          </TabsContent>
        </Tabs>
    </PageShell>
  );
};

export default Appointments;
